import { Button, Tooltip } from "@nu-assist/ui";
/**
 * `Criteria: …` — the one line that answers "why THESE contacts and not
 * others", on a full-width row of its own.
 *
 * It got its own row because it is the only fact on this header that grows
 * without bound: a Company Map over ten companies, each with a role and a year
 * range, is a paragraph. Inline beside the type pill it pushed `Run by` off the
 * readable part of the line and then wrapped into it.
 *
 * It also inherited the type COLOUR from the Search block, which lost its
 * bounding box in the same pass. That is the better home for it: every type
 * renders the same Search block, so tinting that one said nothing, while this
 * row exists only on the types that HAVE criteria and says something different
 * on each.
 *
 * The label is `Criteria:` on every type, not `Company Map criteria:` — the
 * pill one line up already names the type, and the short label leaves the width
 * for the part that grows.
 *
 * A raw import renders here too, in the slot this file reserved for it in prose
 * — it used to end "a raw import renders NOTHING here … when the extension
 * starts sending one, this is where it goes". The premise of that sentence was
 * wrong rather than merely out of date: Baby Chops has ALWAYS written a
 * `Search Query` column (`exports/to-csv.ts`), and it was the INGESTION that
 * dropped it. Migration 0036 gives it `imports.search_criteria` and
 * `SearchCriteriaBox` reads it (Trello #732).
 *
 * It renders ABOVE the type-specific arm and in raw's palette rather than the
 * host type's: where the people came from precedes what was then done to them,
 * and the fact belongs to the capture, not to the file derived from it.
 *
 * The absence rule survives unchanged and now covers three states rather than
 * one: an import from before 0036 (nothing was backfilled), a file without the
 * column, and an inline `Send to NU Assist` upload, whose JSON payload carries
 * no query at all. In all three the row renders NOTHING — a row reading
 * `Criteria: —` would be chrome spent on an absence.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Info, Sparkles } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";

import { BctApiError, type BctClient, type BctCompanyResponse } from "../../../clients/bct.client";
import { usePromptViewerAllowed } from "../../../features/ai-review-prompt/access";
import { CompanyMapPromptModal } from "../../../features/company-map-prompt/CompanyMapPromptModal";
import { CriteriaPanel, type Criterion } from "../ai-review/CriteriaPanel";
import { NEUTRAL_TAG_THEME, type TagTheme } from "../imports/tagDefs";

function companyYearLabel(c: BctCompanyResponse): string | null {
  if (c.start_year != null && c.end_year != null) return `${c.start_year}–${c.end_year}`;
  if (c.start_year != null) return `${c.start_year}–present`;
  if (c.end_year != null) return `up to ${c.end_year}`;
  return null;
}

/** `below` is a SEPARATE slot on purpose, not a second child.
 *
 *  The inner element is a `<span>` with `items-baseline`, which is right for
 *  the label-plus-value row it exists to lay out and wrong for anything after
 *  it. Passing the rerun action as a normal child put a `<div>` inside that
 *  `<span>` — invalid content, and rendered as one more baseline-aligned flex
 *  item to the RIGHT of the chips, with its `mt-2` nudging it out of alignment
 *  instead of onto its own line. The comment two dozen lines down said "BELOW
 *  THE CRITERIA" and was false in this arm for eight review rounds, because no
 *  test rendered the chips arm and the rerun button together
 *  (round 9, Agent A §3.1). */
function Shell({
  theme,
  children,
  below,
  label = "Criteria",
  testId = "criteria-box",
  className = "mb-4",
  block = false,
}: {
  theme: TagTheme;
  children: React.ReactNode;
  below?: React.ReactNode;
  /**
   * Defaults to the bare `Criteria`, which is right for the two arms whose
   * TYPE the pill one line up already names — repeating "Company Map" or "AI
   * Review" here would spend the width that the part which grows needs.
   *
   * The raw arm overrides it. A raw import has no pill saying where its people
   * came from, so `Criteria:` alone would leave the reader to guess whether the
   * string is a review's rubric or the search that produced the list. It is the
   * latter, and the label is the only place that can say so.
   */
  label?: string;
  /**
   * Defaults to the shared `criteria-box`, which fifteen pre-existing tests
   * query with the SINGULAR `getByTestId` (19 query sites across 16 tests once
   * this PR's own is counted — the reviewer who raised this first said eight
   * and withdrew it, having grepped call sites rather than tests). Those passed
   * only because no fixture set
   * `search_criteria`; once the raw arm renders alongside a type arm there are
   * two, and `getByTestId` throws on multiple. The raw arm therefore takes its
   * own id rather than making every one of those call sites a landmine
   * (/pr-review iter 1 on PR #801, Agent C).
   */
  testId?: string;
  /**
   * The box's OUTER spacing, and nothing else — the border, padding and palette
   * are the Shell's own and are not overridable.
   *
   * It exists because `mb-4` is the right gap under a card stacked on a page
   * and the wrong one inside `ExportToThrivePanel`, whose column already spaces
   * its children with `gap-4`; the two compose to 32px and read as a break in a
   * modal that is fighting for vertical room. The panel passes `""`.
   */
  className?: string;
  /**
   * Render `children` as a SIBLING of the label rather than inside the
   * baseline `<span>` that lays the two out on one line.
   *
   * Not cosmetic — it is a validity fix. That `<span>` is phrasing content, and
   * the raw arm's child is a `<details>`, which is flow content. This file's own
   * docblock twenty lines up records what that nesting cost the last time
   * (`/pr-review` round 9): a `<div>` inside the `<span>`, rendered as one more
   * baseline-aligned flex item with its margin pushing it out of line, and a
   * comment claiming the opposite that survived eight review rounds because no
   * test rendered the arm. Raised again here by `/pr-review` iter 5, Agent C —
   * same file, same `<span>`, a different child.
   *
   * The alternative was a third copy of the container classes, which is what
   * the sentences arm already does. One `Shell` with a layout switch beats
   * three literals that can drift apart.
   */
  block?: boolean;
}) {
  const labelEl = <span className={`text-label ${theme.cardLabel}`}>{label}:</span>;
  return (
    <div
      data-testid={testId}
      className={`${className} rounded-md border px-3 py-2 ${theme.cardBorder} ${theme.cardSurface}`}
    >
      {block ? (
        <div className="text-body-sm text-text">
          {labelEl}
          {children}
        </div>
      ) : (
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-body-sm text-text">
          {labelEl}
          {children}
        </span>
      )}
      {below}
    </div>
  );
}

/** Raw import: the LinkedIn Recruiter filters the capture actually ran with.
 *
 * The only arm that fetches nothing — the string ships on the import payload,
 * so there is no loading state and no row that appears a beat after the page.
 *
 * Trimmed before the emptiness check because the backend's `null` and a file
 * whose cell held only spaces are the same fact to a reader, and the worker
 * already normalizes the blank case to `null` on the write path; this is the
 * read-side half of the same rule.
 */
export function SearchCriteriaBox({
  searchCriteria,
  className,
}: {
  searchCriteria: string | null | undefined;
  /** Outer spacing, forwarded to `Shell`. See its `className` docblock. */
  className?: string;
}) {
  const criteria = searchCriteria?.trim();
  if (!criteria) return null;

  return (
    // ALWAYS the raw palette — there is deliberately no `theme` prop to get
    // this wrong with. The string describes the CAPTURE, which is a raw-import
    // fact; tinting it with the host type would say the query belongs to the
    // Company Map or the review, and it belongs to neither.
    //
    // `NEUTRAL_TAG_THEME` is what `tagDefs` documents as "the dressing an
    // untyped list gets — a Raw Import", and it IS enforced: `tagDefs.test.ts`
    // asserts `NEUTRAL_TAG_THEME` equals `TAG_DEF_BY_VALUE.raw.theme`, and has
    // since PR #743. Two object literals, one test holding them together.
    //
    // This comment briefly said the opposite. /pr-review iter 1 read the two
    // literals as unguarded, I implemented that without checking, and iter 2
    // retracted it — the reviewer found his own test. Taking a reviewer's claim
    // on trust is the same failure as taking my own on trust.
    <Shell
      theme={NEUTRAL_TAG_THEME}
      label="Search Criteria"
      testId="search-criteria-box"
      className={className}
      block
    >
      {/* COLLAPSED BY DEFAULT, and the reason is a measurement rather than a
          preference: Jared's real Baby Chops export carries a 2,242-character
          boolean, which filled most of the header on app.local and pushed the contacts table
          — the point of this page — below the fold. Every fixture in the test
          suite used ~44 characters, so nothing here saw it until a real file was
          uploaded (Lucas, 2026-09-01).

          NOT a `line-clamp`. Trello #711 cl.4 item 6 removed exactly that from
          `ContactsTable`'s annex rationales, with a test pinning its absence,
          and restoring it one component over would undo that decision sideways.
          A `<details>` hides nothing: the whole string is in the DOM, reachable
          in one click, keyboard-operable for free, and no state to get wrong. */}
      <details className="group min-w-0">
        {/* The `<summary>` is the ONLY thing that toggles a `<details>`, so it
            has to stay on screen in both states. It briefly did not: the
            `group-open:hidden` that kept the preview from sitting above the full
            text was on the SUMMARY itself, which meant opening the row deleted
            its own close button and the query could not be collapsed again
            (Lucas, 2026-09-01). The hiding moved inward — the summary is
            permanent, its CONTENTS swap. */}
        <summary
          data-testid="search-criteria-summary"
          className="flex cursor-pointer list-none items-center gap-2 text-text-secondary [&::-webkit-details-marker]:hidden"
        >
          {/* The affordance, and the ONLY thing left in the summary once the
              row is open — which is the point: something has to stay clickable
              or the row cannot be collapsed again, and words there would read
              as content rather than as a control. Rotates to point up when
              open, so it says "collapse" in the same place it said "expand".

              FIRST, not last: it is the fixed element of the two, and trailing
              the preview it would slide left the moment that preview vanished,
              moving the control out from under the cursor that just clicked it.

              `aria-hidden` on the icon with an `sr-only` label beside it: a
              rotated glyph has no accessible name, and the summary would
              otherwise announce nothing at all once the preview is hidden. */}
          <span className="sr-only">{"Show or hide the full query"}</span>
          <ChevronDown
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-text-muted transition-transform group-open:rotate-180"
          />
          {/* Closed: the START of the query, not a character count. The first
              clause — `Job titles: ("Chief Executive Officer" …` — is what tells
              a recruiter WHICH search this was, and a count says nothing about
              the value it describes (Lucas, 2026-09-01).

              `line-clamp-1` is the house spelling for a one-line box;
              `EnrichData.tsx` documents why it beats `truncate` here. This is
              NOT the clamp Trello #711 cl.4 item 6 removed — that one clipped a
              value with no way to see the rest. The whole string is one click
              away, and a test pins that it is all still in the DOM. */}
          <span
            data-testid="search-criteria-preview"
            // HIDDEN FROM ASSISTIVE TECH, and this is the correction of a claim
            // made two commits ago rather than a new precaution. The comment
            // below cites `EnrichData.tsx` as the authority for `line-clamp-1`,
            // and what that file actually says (`:142-146`) is that the clamp is
            // "a purely visual clip, the whole address stays in the DOM and the
            // accessibility tree". So the preview was not a preview to a screen
            // reader: it was the `<summary>`'s accessible NAME, and on Jared's
            // real export that name is 2,242 characters — a control announcing
            // an entire boolean query before saying what it does.
            //
            // The name is now the `sr-only` label alone. The query itself stays
            // reachable the way `<details>` intends: expand, and the full text
            // below is announced. (`/pr-review` iter 5, Agent C, [Major].)
            aria-hidden="true"
            className="line-clamp-1 min-w-0 flex-1 break-words group-open:hidden"
          >
            {criteria}
          </span>
        </summary>
        <span data-testid="search-criteria" className="mt-1.5 block break-words">
          {criteria}
        </span>
      </details>
    </Shell>
  );
}

/** Company Map: the companies and year ranges the run matched on, and the
 *  roles it tracked. A run saved before AI role categories has no
 *  `roles_description`; its per-company role is not in the contract any more,
 *  so it shows the companies alone rather than a guess.
 *
 *  "View prompt" opens the run's model calls (`CompanyMapPromptModal`) for
 *  viewers the audience switch allows (`usePromptViewerAllowed`). Portalled to
 *  `<body>`, so the dialog does not inherit this row's palette. */
export function BctCriteriaBox({
  importId,
  bctClient,
  theme = NEUTRAL_TAG_THEME,
}: {
  importId: string;
  bctClient: BctClient;
  theme?: TagTheme;
}) {
  const query = useQuery({
    queryKey: ["bct-run-for-import", importId],
    queryFn: () => bctClient.getBctRunForImport(importId),
    // Don't retry on 404 (no BCT run for this import) or on 2nd+ attempt for
    // other errors — a failed criteria row is non-critical.
    retry: (count, err) => count < 2 && (err instanceof BctApiError ? err.status !== 404 : true),
    staleTime: 5 * 60 * 1000,
  });
  const viewerAllowed = usePromptViewerAllowed();
  const [promptOpen, setPromptOpen] = useState(false);

  const isNotFound = query.error instanceof BctApiError && query.error.status === 404;
  // Silent on 404 (no run for this import) and while loading: a row that
  // appears a beat after the page would shift everything under it.
  if (query.isLoading || isNotFound || !query.data) {
    if (query.isError && !isNotFound) {
      return (
        <Shell theme={theme}>
          <span className="text-text-secondary">Could not load Company Map criteria.</span>
        </Shell>
      );
    }
    return null;
  }

  const { id: runId, companies, roles_description: rolesDescription } = query.data;
  if (companies.length === 0) return null;

  const viewPrompt = viewerAllowed && (
    <div className="mt-2">
      <Button variant="secondary" data-testid="bct-view-prompt" onClick={() => setPromptOpen(true)}>
        View prompt
      </Button>
      {promptOpen &&
        createPortal(
          <CompanyMapPromptModal
            client={bctClient}
            source={{ kind: "run", id: runId }}
            onClose={() => setPromptOpen(false)}
          />,
          document.body
        )}
    </div>
  );

  return (
    <Shell theme={theme} below={viewPrompt}>
      <span data-testid="bct-detail-companies">
        {companies.map((c) => [c.name, companyYearLabel(c)].filter(Boolean).join(" · ")).join(", ")}
      </span>
      {rolesDescription ? (
        <span data-testid="bct-detail-roles" className="text-text-secondary">
          Roles: {rolesDescription}
        </span>
      ) : null}
    </Shell>
  );
}

/** AI Review: the criteria the review scored against.
 *
 * TWO SHAPES, and which one you get is about what could be resolved, not about
 * preference. Trello #711 cl.5 asked for the criteria as the SENTENCES the
 * recruiter typed, one per line, "read-only with the same component as the
 * form" — because reading "Fit", "Seniority" across a header row is what Jared
 * was complaining about. That is `criteria`, from the run's `criteria_config`,
 * rendered through `CriteriaPanel` so a criterion the creator can express is
 * one this can display, by construction.
 *
 * The COLUMNS remain the fallback, and #780's argument for them is why they are
 * still here rather than deleted: `criteria_config` lives on the `ai_reviews`
 * row, which a legacy or re-applied review can lack entirely, while
 * `detail.annex_columns` ships free on the import payload. An import with five
 * score columns and no resolvable run would otherwise show nothing at all —
 * strictly worse than the column names.
 *
 * So: sentences when they resolve, chips when they do not, never both. Merged
 * from the two PRs that arrived on this card within a day of each other (Lucas,
 * 2026-08-26) — #780 shipped the chips here and cl.5 shipped the sentences in a
 * card of their own lower down the page, which would have rendered BOTH.
 *
 * This SUBSUMES the old `AnnexBar`, which rendered the same column names as
 * chips in its own bordered strip directly below. Two boxes, same list, no
 * difference in behaviour (neither was clickable).
 */
export function AiReviewCriteriaBox({
  columns,
  criteria,
  criteriaLoading = false,
  canFeedAi = false,
  onRerun,
  theme = NEUTRAL_TAG_THEME,
}: {
  columns: readonly { key: string; label: string }[];
  /** The run's own criteria, when they could be resolved. Empty/absent falls
   *  back to `columns`. */
  criteria?: readonly Criterion[];
  /** The run's criteria are still being fetched. Without this the box rendered
   *  the `columns` chips first and swapped to the sentences ~100 ms later — a
   *  green flash that read as the form remounting (local QA, PR #1016). While
   *  loading, the box draws its sentences frame with a placeholder instead, so
   *  the chips only ever appear when the criteria really could not be resolved. */
  criteriaLoading?: boolean;
  /** This list's file exists, so a rerun would not 409. */
  canFeedAi?: boolean;
  /** Open the AI Review creator on this list with its criteria carried and
   *  PINNED, so changing the people list there keeps them. One door — this used
   *  to sit beside a second CTA that differed only in which param it sent. */
  onRerun?: () => void;
  theme?: TagTheme;
}) {
  const hasSentences = (criteria?.length ?? 0) > 0;
  const waiting = !hasSentences && criteriaLoading;
  if (!hasSentences && !waiting && columns.length === 0) return null;

  // The rerun action lives BELOW THE CRITERIA and only on a review file
  // (Lucas, 2026-08-26). It used to sit in the page header on every list type.
  // Two things moved it: "Edit criteria and rerun" is meaningless where there
  // are no criteria to edit, and the action reads as what it is directly under
  // the criteria it reopens. A Raw Import therefore has NO button — it is
  // reviewed from the AI Review page's picker or the imports-table row action.
  //
  // The reassurance sits on an info icon BESIDE the button, not around it.
  // That distinction is the whole of cl.4 §D5: wrapping an interactive trigger
  // puts a second tab stop before the page's action and hangs the description
  // on the wrapper span, so a screen reader focused on the BUTTON announces
  // nothing. A separate icon is its own focusable trigger, opens on keyboard
  // focus as well as hover, and is exactly what `Tooltip` is scoped to.
  //
  // ONE button, and it used to be two. `Run on another list…` sat right here
  // in the same row behind the same gate, differing from its neighbour only in
  // which query param it sent — a distinction encoded in the URL and nowhere
  // in the labels, so the pair read as two spellings of "run this rubric
  // again". The surviving button now sends BOTH params, which is what makes
  // "rerun this list" and "run it on another one" the same door: you arrive
  // with this list selected and the criteria pinned, and picking a different
  // list keeps them.
  //
  // It is also what fixed the criteria vanishing on a list change — see the
  // `onRerun` comment in `ImportDetailPage.tsx` for the mechanism.
  //
  // NO `hasSentences` CONDITION, deliberately, and this reverses the rule that
  // stood here. The carry button used to need it, on the reasoning that the
  // chips arm renders precisely when the run's criteria could not be resolved,
  // so there is nothing to carry. True — but the effect was that the row
  // silently dropped to one button on those files, so the pair meant different
  // things depending on which file you were looking at. With one button the
  // argument inverts: rerunning a file whose criteria would not load is still
  // a thing a recruiter does, and it lands on the empty form with the
  // page's own "couldn't load the criteria" notice, which is the honest
  // outcome rather than a missing control.
  const rerun = canFeedAi && onRerun && (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button variant="secondary" data-testid="detail-feed-ai-review" onClick={onRerun}>
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        Edit criteria and rerun
      </Button>
      <span data-testid="detail-feed-ai-review-info">
        <Tooltip
          content="Creates a new people list. This one is kept."
          side="top"
          // Same construction as every other note in this view — click pins,
          // hover still previews. Was hover-only, so on touch it was
          // unreachable and a click did nothing.
          clickToPin
        >
          {/* `role="img"` is not decoration: `Tooltip` wraps its child in a
              `tabIndex={0}` span with no name of its own, so this label IS the
              trigger's accessible name. An `<svg>` carrying `aria-label`
              without a role is the one spelling assistive tech may drop. */}
          <Info className="h-4 w-4 text-text-muted" role="img" aria-label="About rerunning" />
        </Tooltip>
      </span>
    </div>
  );

  // The SENTENCES arm breaks the Shell's one-line layout on purpose: criteria
  // are one per line (cl.5), so the label sits above them rather than beside.
  if (hasSentences || waiting) {
    return (
      <div
        // Deliberately the SAME literal `Shell` defaults to, not the `testId`
        // prop — this arm does not render through `Shell` and has no access to
        // it. The two must stay in step: an arm that wanted its own id would
        // have to change both places, which is why they are named here rather
        // than left to be discovered (/pr-review iter 2, Agent A).
        data-testid="criteria-box"
        className={`mb-4 rounded-md border px-3 py-2 ${theme.cardBorder} ${theme.cardSurface}`}
      >
        <span className={`text-label ${theme.cardLabel}`}>Criteria:</span>
        <div
          data-testid="ai-review-criteria"
          data-arm={waiting ? "loading" : "sentences"}
          className="mt-1.5"
        >
          {waiting ? (
            <div
              data-testid="ai-review-criteria-loading"
              role="status"
              aria-label="Loading criteria"
              className="h-10 w-full max-w-xl animate-pulse rounded-sm bg-surface-raised"
            />
          ) : (
            <CriteriaPanel criteria={criteria as Criterion[]} readOnly />
          )}
        </div>
        {rerun}
      </div>
    );
  }

  return (
    <Shell theme={theme} below={rerun}>
      <span
        data-testid="ai-review-criteria"
        data-arm="chips"
        className="flex flex-wrap items-center gap-1.5"
      >
        {columns.map((col) => (
          <span
            key={col.key}
            // Named for THIS component, not for the `AnnexBar` it replaced —
            // the old id outlived the component by three iterations
            // (`/pr-review` §7(c)).
            data-testid="criteria-chip"
            className="inline-flex items-center rounded-pill bg-accent-muted px-2 py-0.5 text-caption text-accent-ink"
          >
            {col.label}
          </span>
        ))}
      </span>
    </Shell>
  );
}
